<script setup lang="ts">
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import { openDebugTools } from "@verbleif/shared/src/components/DebugTools/useDebugTools";
import Verbleif from "@web/features/Navbar/verbleif_v.svg?component";
import NavbarSettings from "@web/features/NavbarSettings/NavbarSettings.vue";
import { useI18n } from "vue-i18n";
import { useNavbar } from "./useNavbar";

const {
  t,
  version,
  items,
  impersonationStore,
  locationStore,
  pendingInvites,
  currentActiveTimeEntry,
  trackedTime,
  goToTimeEntry,
  chooseLocation,
  switchTheme,
  clearImpersonation,
  stopAndGoToTimeEntry,
  themeSwitchLabel,
} = useNavbar();
// Hyphenation needs the label language; without it a long word only wraps mid-word.
const { locale } = useI18n();
</script>

<template>
  <div
    class="max-md:hidden fixed left-0 top-0 z-5 w-[95px] h-screen flex flex-col items-center select-none py-6"
    :style="{ backgroundColor: 'var(--primary)' }"
  >
    <!-- Top: Logo + contextual items -->
    <div class="mb-4" @click.shift="openDebugTools()">
      <Verbleif aria-hidden="true" />
    </div>

    <button
      v-if="impersonationStore.isImpersonating.value"
      type="button"
      class="flex flex-col items-center justify-center text-white w-full py-2 cursor-pointer font-bold transition-colors duration-100 hover:text-(--secondary) focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white outline-hidden"
      @click="clearImpersonation"
    >
      <VIcon class="text-xl" name="venetian-mask" />
      <span class="text-[11px] mt-1 text-center">
        {{ t("navbar.stop-impersonation") }}
      </span>
    </button>

    <button
      v-if="locationStore.hasMultipleLocations.value"
      type="button"
      class="flex flex-col items-center justify-center text-white w-full py-2 cursor-pointer transition-colors duration-100 hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white outline-hidden"
      :title="t('navbar.change-location')"
      :aria-label="t('navbar.change-location')"
      @click="chooseLocation"
    >
      <VTooltip :disabled="!pendingInvites" :content="pendingInvites ? t('navbar.pending-invites', { count: pendingInvites }) : ''">
        <template #reference>
          <span class="relative block">
            <VIcon class="text-xl text-white" name="map-pin" />
            <span v-if="pendingInvites" class="absolute font-bold -right-3 -top-2.5 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {{ pendingInvites }}
            </span>
          </span>
        </template>
      </VTooltip>
      <span class="w-full px-1 text-[11px] leading-tight mt-1.5 text-center hyphens-auto break-words" :lang="locale">
        {{ locationStore.selectedLocationObject.value?.name || t("navbar.change-location") }}
      </span>
    </button>

    <div class="border-t border-white/10 w-3/4 my-2" />

    <!-- Middle: Navigation links -->
    <nav class="flex-1 w-full flex flex-col justify-center">
      <router-link
        v-for="item in items"
        :key="item.key"
        v-slot="{ href, navigate, isActive }"
        custom
        :to="item.to"
      >
        <a
          :href="href"
          class="relative flex flex-col items-center cursor-pointer w-full py-5 gap-1.5 transition-all duration-100 hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white outline-hidden"
          :class="isActive && 'bg-white/10'"
          :aria-current="isActive ? 'page' : undefined"
          @click="navigate"
        >
          <span v-if="isActive" class="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-[3px] rounded-r-full bg-white" aria-hidden="true" />
          <VIcon :name="item.icon" class="text-2xl text-white" />
          <span class="w-full px-1 text-[11px] leading-tight text-center hyphens-auto break-normal" :lang="locale" :class="isActive ? 'text-white font-medium' : 'text-white/70'">
            {{ item.label }}
          </span>
        </a>
      </router-link>
    </nav>

    <div class="border-t border-white/10 w-3/4 my-2" />

    <!-- Bottom: Time entry, avatar, utilities, version -->
    <div class="flex flex-col items-center gap-3">
      <div
        v-if="currentActiveTimeEntry"
        class="text-white flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-primary-400 hover:bg-primary-700"
      >
        <button
          type="button"
          class="cursor-pointer rounded-xs tabular-nums focus-visible:ring-2 focus-visible:ring-white outline-hidden"
          @click="goToTimeEntry"
        >
          {{ trackedTime }}
        </button>
        <button
          type="button"
          class="rounded-full bg-red-400 w-5 h-5 flex items-center justify-center hover:bg-red-500 cursor-pointer focus-visible:ring-2 focus-visible:ring-white outline-hidden"
          :aria-label="t('tasks.TimeEntry.stop_timer')"
          :title="t('tasks.TimeEntry.stop_timer')"
          @click="stopAndGoToTimeEntry"
        >
          <span class="w-2 h-2 bg-white rounded-xs" aria-hidden="true" />
        </button>
      </div>

      <NavbarSettings />

      <div class="flex gap-4">
        <a
          class="cursor-pointer text-white/70 hover:text-white transition-colors duration-100 rounded-xs focus-visible:ring-2 focus-visible:ring-white outline-hidden"
          href="https://docs.verbleif.com"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="t('navbar.manual')"
          :title="t('navbar.manual')"
        >
          <VIcon class="text-sm" name="book" />
        </a>
        <button
          type="button"
          class="theme-switch cursor-pointer text-white/70 hover:text-white transition-colors duration-100 rounded-xs focus-visible:ring-2 focus-visible:ring-white outline-hidden"
          :aria-label="themeSwitchLabel"
          :title="themeSwitchLabel"
          @click="switchTheme"
        >
          <VIcon class="text-sm icon" name="contrast" />
        </button>
      </div>

      <div class="text-white/50 text-[10px] font-medium">
        {{ version }}
      </div>
    </div>
  </div>
</template>
